import React from "react";
import clsx from "clsx";
import { PressEvent, Button as ReactAriaButton } from "react-aria-components";
import { ButtonContext, useContextProps } from "react-aria-components";

import Icon, { type IconName } from "../icon";

import styles from "./button.module.css";

/**
 * The tones a *text-bearing* button can wear.
 *
 * `avatar-surface` retires the 10 copy-pasted `className` chrome sites (a
 * primary button re-skinned onto `--pallet-role-surface-avatar`).
 * `danger-container` / `success-container` are the low-emphasis container
 * tones the row-action buttons hand-rolled.
 *
 * Deliberately does not include the `link` arm's `accent` / `underline`
 * tones: those change box model, not just colour, so they live only on the
 * `link` arm (see `ButtonKind`) and must not leak onto `text-only` et al.
 */
type ButtonTone =
  | "primary"
  | "secondary"
  | "tertiary"
  | "danger"
  | "avatar-surface"
  | "danger-container"
  | "success-container";

/** The chromes an *icon-only* button can wear. Exactly three, all measured. */
type IconChrome = "tertiary" | "bare" | "filled";

/** The tones the `link` arm can wear. See `ButtonKind`'s `link` member. */
type LinkTone = "accent" | "underline";

/** The orthogonal density modifier. Lives on the four arms that have a box
 *  model to size — absent from `link`, which has none. */
type Scale = "standard" | "inline" | "compact";

/**
 * `kind` is the **content shape**, and each arm declares which chromes are
 * legal for that shape — so an illegal (chrome x content) pair does not
 * compile. Pairs with zero call sites are deliberately absent; widening the
 * union later is a one-line edit, and a call site that seems to need a missing
 * pair is a conversation, not a cast.
 *
 * There is no `empty` arm: a button with neither text nor icon had zero call
 * sites and existed only because the old type permitted it.
 */
export type ButtonKind =
  | {
      kind: "text-only";
      text: string;
      variant: ButtonTone;
      /** Single-line ellipsis on the button's own label. */
      overflow?: "truncate";
      scale?: Scale;
    }
  | {
      kind: "text-icon-left";
      text: string;
      icon: IconName;
      variant: ButtonTone;
      overflow?: "truncate";
      scale?: Scale;
    }
  | {
      /** Tertiary only, by measurement — one call site in the repo. */
      kind: "text-icon-right";
      text: string;
      icon: IconName;
      variant: "tertiary";
      overflow?: "truncate";
      scale?: Scale;
    }
  | {
      kind: "icon-only";
      icon: IconName;
      /**
       * REQUIRED: with no text there is nothing else to name the button, so
       * this is the accessible name and it flows to `Icon`'s `label`.
       */
      label: string;
      variant: IconChrome;
      scale?: Scale;
    }
  | {
      /**
       * An in-place action inside a sentence, a banner, or a filter bar —
       * "Clear all", "Dismiss" — where a real button's plate and input
       * height would dominate the row. Still a `<button>`: if the thing
       * being done is navigation, use a link.
       *
       * A `kind` arm, not a `variant`/tone value: `variant` on every other
       * arm is a *tone* axis (colour and fill only), while a link changes
       * *box model*. Folding it into `variant` would make `variant` mean two
       * different things and legalise meaningless pairs like
       * `scale="compact"` × `variant="link"`. `kind` is already the shape
       * axis, so the link belongs there — and has no `scale` of its own,
       * since `inline`/`compact` set height/padding/font-size on a box this
       * arm does not have.
       */
      kind: "link";
      text: string;
      variant: LinkTone;
    };

type ButtonBase = {
  id?: string;
  type?: "submit" | "button";
  isDisabled?: boolean;
  /**
   * Unchanged: this is a prop-shape change, not a behaviour change. No call
   * site uses the event argument, but narrowing it is not this phase's job.
   */
  onPress?: (event?: PressEvent) => void;
  /**
   * Load-bearing on every arm. `slot={null}` opts a Button out of an ancestor
   * react-aria provider (a `SearchField` otherwise silently adopts it as its
   * clear button), and `slot="close"` is how a `Dialog` wires its dismiss.
   */
  slot?: string | null;
  // NO className. There is no escape hatch: a call site that needs one is a
  // missing variant, and the variant gets added here.
};

export type ButtonProps = ButtonKind & ButtonBase;

/**
 * The union flattened into one optional-keyed shape, for the render body only.
 *
 * The union is the *caller's* contract; inside the component every arm is
 * handled, so one flat read is clearer than four narrowing branches.
 */
type FlatButtonProps = ButtonBase & {
  kind: ButtonKind["kind"];
  text?: string;
  icon?: IconName;
  label?: string;
  variant: ButtonTone | IconChrome | LinkTone;
  overflow?: "truncate";
  scale?: Scale;
};

/**
 * A `switch` over whole literals, not `styles[variant]`: the tone names are
 * kebab-case at the API and camelCase in the CSS module, and an unmapped tone
 * is a compile error here rather than a missing class at runtime.
 */
function toneClass(variant: ButtonTone | IconChrome | LinkTone): string {
  switch (variant) {
    case "primary":
      return styles.primary;
    case "secondary":
      return styles.secondary;
    case "tertiary":
      return styles.tertiary;
    case "danger":
      return styles.danger;
    case "avatar-surface":
      return styles.avatarSurface;
    case "danger-container":
      return styles.dangerContainer;
    case "success-container":
      return styles.successContainer;
    case "bare":
      return styles.bare;
    case "filled":
      return styles.filled;
    case "accent":
      return styles.accent;
    case "underline":
      return styles.underline;
  }
}

/**
 * `.button` and `.linkButton` cannot share a base class: one is a fixed-height
 * flex box with padding, a radius and a plate; the other is `display: inline`
 * with no padding, border or background. A `switch` over the whole `kind`
 * literal keeps an unmapped arm a compile error rather than a missing class.
 */
function baseClass(kind: ButtonKind["kind"]): string {
  switch (kind) {
    case "link":
      return styles.linkButton;
    case "text-only":
    case "text-icon-left":
    case "text-icon-right":
    case "icon-only":
      return styles.button;
  }
}

function scaleClass(scale: Scale | undefined): string | undefined {
  switch (scale) {
    case "inline":
      return styles.inline;
    case "compact":
      return styles.compact;
    default:
      return undefined;
  }
}

export default React.forwardRef(
  (props: ButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    // Merge the local props and ref with the ones provided via context.
    [props, ref] = useContextProps(props, ref, ButtonContext);

    const {
      kind,
      text,
      icon,
      label,
      variant,
      overflow,
      scale,
      // Everything else is behavioural and belongs on the element. A react-aria
      // provider can inject extras (`excludeFromTabOrder`, `aria-label`, …);
      // the rest spread carries them through even though the type does not
      // enumerate them.
      ...domProps
    } = props as FlatButtonProps;

    const glyph =
      icon === undefined ? null : (
        <Icon key="icon" name={icon} size={18} label={label} />
      );

    const body =
      overflow === "truncate" ? (
        <span key="text" className={styles.truncateText}>
          {text}
        </span>
      ) : (
        text
      );

    let content: React.ReactNode[];
    switch (kind) {
      case "icon-only":
        content = [glyph];
        break;
      case "text-icon-right":
        content = [body, glyph];
        break;
      case "text-icon-left":
        content = [glyph, body];
        break;
      case "text-only":
        content = [body];
        break;
      case "link":
        content = [body];
        break;
    }

    return (
      <ReactAriaButton
        {...domProps}
        className={clsx(
          baseClass(kind),
          toneClass(variant),
          scaleClass(scale),
          {
            [styles.iconOnly]: kind === "icon-only",
            [styles.iconLeft]: kind === "text-icon-left",
            [styles.iconRight]: kind === "text-icon-right",
            [styles.truncate]: overflow === "truncate",
          },
        )}
        ref={ref}
      >
        {content}
      </ReactAriaButton>
    );
  },
);
